const editorInstances = new WeakMap();

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'summary',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class CotigoEditorDrawer {
  constructor(shell, panel) {
    this.shell = shell;
    this.panel = panel;
    this.toggle = shell.querySelector(`[data-cotigo-editor-toggle][aria-controls="${CSS.escape(panel.id)}"]`);
    this.overlay = panel.previousElementSibling?.matches('[data-cotigo-editor-overlay]') ? panel.previousElementSibling : null;
    this.closeButton = panel.querySelector('[data-cotigo-editor-close]');
    this.lastTrigger = null;
    this.wasScrollLocked = false;

    if (!this.toggle || !this.overlay || !this.closeButton) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.bind();
    this.handleResize();
  }

  bind() {
    this.toggle.addEventListener('click', this.handleToggle);
    this.closeButton.addEventListener('click', this.handleClose);
    this.overlay.addEventListener('click', this.handleClose);
    this.panel.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    this.close({ restoreFocus: false });
    this.toggle?.removeEventListener('click', this.handleToggle);
    this.closeButton?.removeEventListener('click', this.handleClose);
    this.overlay?.removeEventListener('click', this.handleClose);
    this.panel.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    this.lastTrigger = null;
  }

  isDrawerMode() {
    return this.panel.dataset.cotigoEditorDrawer === 'true' && window.matchMedia('(max-width: 67.999rem)').matches;
  }

  handleToggle(event) {
    if (this.panel.dataset.cotigoEditorOpen === 'true') this.close({ restoreFocus: true });
    else this.open(event.currentTarget);
  }

  handleClose() {
    this.close({ restoreFocus: true });
  }

  handleClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    this.close({ restoreFocus: false });
    this.afterLayout(() => {
      target.scrollIntoView({ block: 'start', inline: 'nearest' });
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
    });
  }

  handleKeydown(event) {
    if (this.panel.dataset.cotigoEditorOpen !== 'true') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close({ restoreFocus: true });
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  handleResize() {
    if (this.isDrawerMode()) {
      this.toggle.hidden = this.panel.hidden;
      this.panel.setAttribute('aria-hidden', this.panel.dataset.cotigoEditorOpen === 'true' ? 'false' : 'true');
      return;
    }

    this.toggle.hidden = true;
    this.close({ restoreFocus: false });
    this.panel.removeAttribute('aria-hidden');
  }

  open(trigger) {
    if (!this.isDrawerMode() || this.panel.hidden) return;
    this.lastTrigger = trigger instanceof HTMLElement ? trigger : null;
    this.wasScrollLocked = document.documentElement.hasAttribute('scroll-lock');
    this.panel.dataset.cotigoEditorOpen = 'true';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.overlay.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    this.afterLayout(() => (this.getFocusable()[0] || this.makePanelFocusable()).focus({ preventScroll: true }));
  }

  close({ restoreFocus = false } = {}) {
    const wasOpen = this.panel.dataset.cotigoEditorOpen === 'true';
    const trigger = this.lastTrigger;
    delete this.panel.dataset.cotigoEditorOpen;
    this.panel.removeAttribute('role');
    this.panel.removeAttribute('aria-modal');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.overlay.hidden = true;
    if (this.isDrawerMode()) this.panel.setAttribute('aria-hidden', 'true');
    else this.panel.removeAttribute('aria-hidden');
    if (wasOpen && !this.wasScrollLocked) document.documentElement.removeAttribute('scroll-lock');
    this.wasScrollLocked = false;
    this.lastTrigger = null;
    if (restoreFocus && wasOpen && this.canFocus(trigger)) trigger.focus({ preventScroll: true });
  }

  trapFocus(event) {
    const focusable = this.getFocusable();
    if (!focusable.length) {
      event.preventDefault();
      this.makePanelFocusable().focus({ preventScroll: true });
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!this.panel.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusable() {
    return Array.from(this.panel.querySelectorAll(focusableSelector)).filter((element) => this.canFocus(element));
  }

  canFocus(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected || element.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(element);
    return !element.matches(':disabled') && style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  makePanelFocusable() {
    if (!this.panel.hasAttribute('tabindex')) this.panel.setAttribute('tabindex', '-1');
    return this.panel;
  }

  afterLayout(callback) {
    window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
  }
}

class CotigoArticleEditor {
  constructor(shell) {
    this.shell = shell;
    this.content = shell.querySelector('[data-cotigo-editor-content]');
    this.drawers = [];
    if (!this.content) return;
    this.buildTocs();
    this.drawers = Array.from(shell.querySelectorAll('[data-cotigo-editor-panel]')).map((panel) => new CotigoEditorDrawer(shell, panel));
    this.shell.dataset.enhanced = 'true';
  }

  destroy() {
    this.drawers.forEach((drawer) => drawer.destroy());
    delete this.shell.dataset.enhanced;
  }

  buildTocs() {
    const usedIds = new Set(Array.from(document.querySelectorAll('[id]')).map((element) => element.id));
    this.shell.querySelectorAll('[data-cotigo-editor-toc]').forEach((tocBlock) => {
      const selector = tocBlock.dataset.cotigoTocLevels === 'h2' ? 'h2' : 'h2, h3';
      const headings = Array.from(this.content.querySelectorAll(selector)).filter((heading) => heading.textContent.trim() && !heading.hasAttribute('data-toc-exclude'));
      const list = tocBlock.querySelector('[data-cotigo-editor-toc-list]');
      if (!list || headings.length < 2) {
        tocBlock.hidden = true;
        return;
      }

      const items = [];
      let parent = null;
      headings.forEach((heading, index) => {
        if (!heading.id) heading.id = this.uniqueHeadingId(heading.textContent, index, usedIds);
        const item = { heading, children: [] };
        if (heading.tagName === 'H3' && parent) parent.children.push(item);
        else {
          items.push(item);
          parent = heading.tagName === 'H2' ? item : null;
        }
      });
      list.replaceChildren(...items.map((item) => this.renderItem(item)));
      tocBlock.hidden = false;
    });
  }

  uniqueHeadingId(text, index, usedIds) {
    const base = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `seccion-${index + 1}`;
    let id = base;
    let suffix = 1;
    while (usedIds.has(id)) id = `${base}-${++suffix}`;
    usedIds.add(id);
    return id;
  }

  renderItem(item) {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${item.heading.id}`;
    link.textContent = item.heading.textContent.trim();
    li.append(link);
    if (item.children.length) {
      const nested = document.createElement('ol');
      nested.append(...item.children.map((child) => this.renderItem(child)));
      li.append(nested);
    }
    return li;
  }
}

function initArticleEditors(root = document) {
  root.querySelectorAll('[data-cotigo-editor-shell]').forEach((shell) => {
    if (!(shell instanceof HTMLElement) || editorInstances.has(shell)) return;
    const instance = new CotigoArticleEditor(shell);
    if (instance.content) editorInstances.set(shell, instance);
  });
}

document.addEventListener('DOMContentLoaded', () => initArticleEditors());
document.addEventListener('shopify:section:load', (event) => initArticleEditors(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-cotigo-editor-shell]').forEach((shell) => {
    editorInstances.get(shell)?.destroy();
    editorInstances.delete(shell);
  });
});
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target.closest('[data-cotigo-editor-block]');
  if (!block) return;
  const panel = block.closest('[data-cotigo-editor-panel]');
  const shell = block.closest('[data-cotigo-editor-shell]');
  if (panel?.dataset.cotigoEditorDrawer === 'true' && panel.dataset.cotigoEditorOpen !== 'true' && window.matchMedia('(max-width: 67.999rem)').matches) {
    const toggle = shell?.querySelector(`[data-cotigo-editor-toggle][aria-controls="${CSS.escape(panel.id)}"]`);
    toggle?.click();
  }
  block.scrollIntoView({ block: 'center', inline: 'nearest' });
});
